import type { ReactNode } from 'react';
import { Scissors } from 'lucide-react';
import { TooltipButton } from './TooltipButton';
import { BoundaryPendingMark } from './BoundaryPendingMark';
import './reader-page-break.css';

/** An explicit boundary action, not a learned boundary or an automatic split.
 * pageNumber is the document-relative page that would start the new document.
 * `pending` is a cut or join here that has been accepted and is still saving. */
export function ReaderPageBreak({
  pageNumber,
  batchPageNumber,
  disabledReason,
  pending,
  notice,
  onSplit,
}: {
  pageNumber: number;
  /** The batch page this break is in front of, for the Saving… mark. */
  batchPageNumber?: number;
  disabledReason?: string | null;
  pending?: 'split' | 'join' | 'undo' | null;
  /** Just joined here: its line, for six seconds, in place of the scissors (owner, 2026-10-30). */
  notice?: ReactNode;
  onSplit: () => void;
}) {
  const label = `Split before page ${pageNumber}`;
  if (pending)
    return (
      <div className="reader-page-break pending" data-before-page={pageNumber}>
        <BoundaryPendingMark kind={pending} pageNumber={batchPageNumber} />
      </div>
    );
  // Its Undo is a control, not paper: a press on the line is never the reader's blank-paper pan.
  if (notice)
    return (
      <div
        className="reader-page-break with-notice"
        data-before-page={pageNumber}
        onPointerDown={(event) => {
          if (event.button === 0) event.stopPropagation();
        }}
      >
        {notice}
      </div>
    );
  return (
    <div className="reader-page-break" data-before-page={pageNumber}>
      <TooltipButton
        type="button"
        className="reader-page-break-action"
        data-telemetry="document-split"
        disabled={Boolean(disabledReason)}
        aria-label={label}
        aria-description={disabledReason ?? undefined}
        tooltip={disabledReason ?? 'Start a new document here · Ctrl+B'}
        draggable={false}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          // A click here is not a blank-paper pan, text selection, or editor blur.
          // Right-drag remains available to the reader's existing zoom gesture.
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={onSplit}
      >
        <Scissors size={16} aria-hidden="true" />
      </TooltipButton>
    </div>
  );
}
